// Convert filter object to URL-friendly string
export function encodeFiltersToUrl(filterObj, allFilters) {
  if ((!filterObj.selected && !filterObj.parent) || !allFilters?.filters)
    return "";

  // Create a map of category IDs to their titles with additional size categories
  const categoryMap = new Map([
    ...allFilters.filters.map((filter) => [
      filter.id.toString(),
      filter.title.toLowerCase(),
    ]),
    // Add custom mappings for size categories
    ["4", "size"],
    ["5", "price"],
    ["6", "size-v2"],
    ["8", "clothing-size"],
    ["9", "shoes-size"],
    ["10", "bra-size"],
  ]);

  // Group filters by category
  const groupedFilters = new Map();

  // Handle selected filters
  if (filterObj.selected) {
    for (const [key, filters] of Object.entries(filterObj.selected)) {
      if (filters && filters.length > 0) {
        const categoryTitle = categoryMap.get(key) || key;
        const titles = filters.map((filter) =>
          encodeURIComponent(
            filter.title
              .toLowerCase()
              .replaceAll("-", "____")
              .replaceAll(/&/g, "__and__")
              .replaceAll(/\s+/g, "-")
          )
        );
        groupedFilters.set(categoryTitle, titles);
      }
    }
  }

  // Convert grouped filters to URL parts
  const urlParts = Array.from(groupedFilters.entries()).map(
    ([category, titles]) => {
      const encodedCategory = encodeURIComponent(
        category
          .replaceAll("-", "____")
          .replaceAll(/&/g, "__and__")
          .replaceAll(/\s+/g, "-")
      );
      return `${encodedCategory}=${titles.join("~")}`;
    }
  );

  // Handle parent categories
  if (filterObj.parent && filterObj.parent.length > 0) {
    const parentsByCategory = new Map();

    filterObj.parent.forEach((parent) => {
      const categoryTitle =
        categoryMap.get(parent.categoryID.toString()) || "category";
      const encodedTitle = encodeURIComponent(
        parent.title
          .toLowerCase()
          .replaceAll("-", "____")
          .replaceAll(/&/g, "__and__")
          .replaceAll(/\s+/g, "-")
      );

      if (!parentsByCategory.has(categoryTitle)) {
        parentsByCategory.set(categoryTitle, []);
      }
      parentsByCategory.get(categoryTitle).push(encodedTitle);
    });

    // Add parent categories to URL parts
    parentsByCategory.forEach((titles, category) => {
      urlParts.push(`parent-${category}=${titles.join("~")}`);
    });
  }

  return urlParts.join("&");
}

// Convert URL-friendly string back to filter object
export function decodeUrlToFilters(urlString, allFilters) {
  const result = { selected: {}, parent: [] };
  if (!urlString || !allFilters?.filters) return result;
  // Create maps for quick lookups with additional size categories
  const categoryIdMap = new Map([
    ...allFilters.filters.map((filter) => [
      filter.title.toLowerCase(),
      filter.id.toString(),
    ]),
    // Add reverse mappings for size categories
    ["size", "4"],
    ["price", "5"],
    ["clothing-size", "8"],
    ["shoes-size", "9"],
    ["bra-size", "10"],
  ]);

  console.log("params", urlString?.replaceAll("____", "-").replaceAll('---',' - '));

  const params = new URLSearchParams(urlString?.replaceAll("clothing____size", "clothing-size")?.replaceAll('shoes____size','shoes-size')?.replaceAll("bra____size", "bra-size"));
  for (const [param, value] of params.entries()) {
    // Handle parent category params
    if (param.startsWith("parent-")) {
      const categoryTitle = param.slice(7); // Remove 'parent-' prefix
      const categoryID = parseInt(categoryIdMap.get(categoryTitle));

      // Split multiple parent values
      const parentTitles = value.split("~");
      parentTitles.forEach((encodedTitle) => {
        const decodedTitle = decodeURIComponent(encodedTitle)
          .replaceAll("____", "-")
          .replaceAll(/__and__/g, "&")
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // Find the matching parent category from allFilters
        const categoryFilters = allFilters.filters.find(
          (f) => f.id === categoryID
        );
        if (categoryFilters) {
          const parentOption = categoryFilters.options.find(
            (opt) => opt.title.toLowerCase() === decodedTitle.toLowerCase()
          );
          if (parentOption) {
            result.parent.push({
              categoryID,
              id: parentOption.id,
              title: parentOption.title,
              isSubCategory: true,
            });
          }
        }
      });
      continue;
    }

    // Handle regular filters
    const categoryId = categoryIdMap.get(param.toLowerCase());
    if (!categoryId) continue;

    // Split multiple values for the category
    const titles = value.split("~");

    console.log("titles", titles);

    titles.forEach((encodedTitle) => {
      const decodedTitle = decodeURIComponent(encodedTitle)
        .replaceAll(/__and__/g, "&")
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
        .replaceAll("____", "-").replaceAll("*percent*","%")

      // Find the matching filter from allFilters based on title
      const categoryFilters = allFilters.filters.find(
        (f) => f.id.toString() === categoryId
      );
      let matchingFilter = null;

      if (categoryFilters) {
        // Search through all options recursively to find the matching title
        const findFilterByTitle = (options) => {
          for (const option of options) {
            if (option.title.toLowerCase() === decodedTitle.toLowerCase()) {
              return option;
            }
            if (option.options?.length > 0) {
              const found = findFilterByTitle(option.options);
              if (found) return found;
            }
          }
          return null;
        };

        matchingFilter = findFilterByTitle(categoryFilters.options);
      }

      if (matchingFilter) {
        const filter = {
          title: matchingFilter.title,
          id: matchingFilter.id,
          options: [],
        };

        if (!result.selected[categoryId]) {
          result.selected[categoryId] = [];
        }
        result.selected[categoryId].push(filter);
      }
    });
  }

  return result;
}

// Example usage:
/*
Input filter object:
{
  selected: {
    "1": [
      { title: "Cuffs", id: 99, options: [] },
      { title: "Belts", id: 101, options: [] }
    ],
    "2": [
      { title: "&Daughter", id: 100887, options: [] },
      { title: "Zara", id: 100972, options: [] }
    ],
    "3": [{ title: "Beige", colorCode: "#F5F5DC", id: 1, options: [] }],
    "4": [{ title: "M", id: 476, options: [] }],
    "6": [{ title: "US 6", id: "US 6", options: [] }]
  },
  parent: [
    { categoryID: 1, id: 2, title: "Bags", isSubCategory: true },
    { categoryID: 1, id: 3, title: "Clothing", isSubCategory: true }
  ]
}

Output URL string:
category=cuffs~belts/daughter~zara&color=beige&size=m&clothing-size=us-6&parent-category=bags~clothing

And vice versa for decoding (requires allFilters object to look up IDs).
*/
